import { html2canvas, createJsPdf } from "@/app/report/common/export/lazyLibs"
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "@/hooks/useSearchParams"
import { isArray, isEmpty, isNil } from "lodash"
import { useTranslation } from "react-i18next"
import { useRecordReportView } from "@/hooks/useRecordReportView"
import type { CreateReportHistoryPayload } from "@/types/reportHistory"

import {
  useReportTemplateDetailQuery,
  useTrendPlayerActivitySummaryQuery,
  useTrendPlayerSummaryQuery,
} from "@/hooks/api/useReportQuery"
import { useActivityParticipatedPlayerListQuery } from "@/hooks/api/useActivityQuery"
import { TrendReportDataType } from "@/types/activity"
import { ReportTemplate, Section, TrendActivityPlayer, TrendPlayerReport } from "@/types/report"
import { useMetricList } from "@/hooks/api/useMetricQuery"
import {
  getDecimalPoint,
  getSummaryImplValue,
  parseSummary,
  parseUnit,
  removeSpecialCharacter,
} from "@/app/report/common/tools"
import {
  GraphSettings,
  ImgData,
  MetricMapType,
  PrintBasicSet,
  ReportCommonProps,
  StatsSettings,
  TypeSection,
} from "@/app/report/common/type"
import {
  DIVE_METRICS,
  REPORT_TYPE,
  SECTION_TYPE,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import { AxisY, ChartData, ChartRow, SectionGraph, Sort } from "@/app/report/common/constructor"
import { SORT_ORDER } from "@/constants/setting"
import { createAccountStore } from "@/stores/account"
import { Unit } from "@/types/setting"
import { useUnitQuery } from "@/hooks/api/useSettingQuery"
import { globalDate } from "@/utils/format"
import { colors } from "@/components/colors"
import useRouter from "@/hooks/router/useRoute"
import Icon from "@/components/icon/Icon"
import Dialog from "@/components/dialog/Dialog"

import InformationSummary from "@/app/report/components/InformationSummary"
import PdfProgressOverlay, { PdfProgressHandle } from "@/app/report/components/PdfProgressOverlay"
import ReportFilter from "@/app/report/components/filter/ReportFilter"
import ChartByPlayer from "@/app/report/components/section/ChartByPlayerSection"
import ChartByActivity from "@/app/report/components/section/ChartByActivitySection"
import CustomTableSection from "@/app/report/components/section/CustomTableSection"
import BasicButton from "@/components/button/BasicButton"

import ExportChartDialog from "@/app/report/components/dialog/ExportChartDialog"
import PrintableChartByActivitySection from "@/app/report/components/section/pdf/PrintableChartByActivitySection"
import PrintableChartByPlayerSection from "@/app/report/components/section/pdf/PrintableChartByPlayerSection"
import PrintableCustomTableSection from "@/app/report/components/section/pdf/PrintableCustomTableSection"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { usePositionRank } from "@/hooks/usePositionRank"
import { buildReportSortKey, createReportSortStore } from "@/stores/reportSort"
import { createReportColumnFilterStore } from "@/stores/reportColumnFilter"
import { sortGraphSectionForExport } from "@/app/report/common/export/sortRows"
import { inlineStylesheetsIntoClone } from "@/app/report/common/export/inlineStylesheets"
import {
  clearHtml2CanvasIgnore,
  markHtml2CanvasIgnoreOutside,
} from "@/app/report/common/export/html2canvasIgnore"
import { PDF_PREVIEW_WIDTH_PX } from "@/app/report/common/export/constants"
import {
  paginate,
  PAGE_MARGIN_MM,
  CHUNK_TYPE,
  A4_WIDTH_MM,
  A4_HEIGHT_MM,
  CONTENT_WIDTH_MM,
} from "@/app/report/common/export/paginate"
import {
  collectPdfTargets,
  measureChunkBox,
  chunkImageSizeMm,
  chunkTypeOf,
  MeasureChunkOptions,
} from "@/app/report/common/export/chunks"
import {
  applyPreviewPagination,
  clearPreviewPagination,
  waitForStableLayout,
  waitForPreviewContainer,
  PreviewPageBox,
} from "@/app/report/common/export/previewPagination"
import PreviewPageLayer from "@/app/report/components/PreviewPageLayer"

import "./trendReport.scss"

const DEFAULT_DETAIL_DATA: TrendReportDataType = {
  name: "-",
  playerCount: "-",
  activityCount: "-",
  activityKeyCount: {
    T: "-",
    L: "-",
    C: "-",
    F: "-",
    R: "-",
    E: "-",
  },
}

const INIT_FILTER = {
  selectedActivityIds: [],
  selectedPlayerIds: [],
  selectedIdfTemplate: [],
}

type TrendReportFilterType = {
  selectedActivityIds: number[]
  selectedPlayerIds: number[]
  selectedIdfTemplate: number[]
}

type TrendReportSourceType = {
  trendReportPlayers: Array<TrendPlayerReport>
  trendActivityPlayerReport: Array<TrendActivityPlayer>
}

// trend 의 상단 요약은 높이가 0/짧게 잡히는 경우가 있어 방어적으로 잰다.
const MEASURE_OPTIONS: MeasureChunkOptions = { robustSummaryHeight: true }

export default function Trend() {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const { selectedIdfTeam } = createAccountStore()
  const router = useRouter()
  const searchParams = useSearchParams()
  const dateStart = useMemo(() => searchParams.get("dateStart"), [searchParams]) ?? ""
  const dateFinish = useMemo(() => searchParams.get("dateFinish"), [searchParams]) ?? ""
  const idfActivity = useMemo(
    () => searchParams.get("idfActivity")?.split(",").map(Number),
    [searchParams],
  )
  const idfPlayer = useMemo(
    () => searchParams.get("idfPlayer")?.split(",").map(Number),
    [searchParams],
  )
  const idfTemplate = useMemo(
    () => searchParams.get("idfTemplate")?.split(",").map(Number),
    [searchParams],
  )

  const [template, setTemplate] = useState<ReportTemplate>()
  const [playersData, setPlayersData] = useState<
    Array<TrendPlayerReport & { image: string | null }>
  >([])
  const [reportSections, setReportSections] = useState<TypeSection[]>([])
  const [source, setSource] = useState<TrendReportSourceType>({
    trendReportPlayers: [],
    trendActivityPlayerReport: [],
  })
  const [filter, setFilter] = useState<TrendReportFilterType>(INIT_FILTER)

  const [exportPdfOpen, setExportPdfOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  // 미리보기에 그릴 A4 페이지 박스. 출력물과 같은 배치 규칙으로 계산한다.
  const [previewPages, setPreviewPages] = useState<PreviewPageBox[]>([])
  const [currentDecimalPoints, setCurrentDecimalPoints] = useState(1)
  const [currentAnnotationOptions, setCurrentAnnotationOptions] = useState(
    VALUE_POSITION_TYPE.HIDE_VALUES,
  )
  const [decimalPoints, setDecimalPoints] = useState(1)
  const [annotationOptions, setAnnotationOptions] = useState(VALUE_POSITION_TYPE.HIDE_VALUES)
  const [pdfLoading, setPdfLoading] = useState(false)
  // 진행률은 오버레이 컴포넌트 내부 상태로 격리 (캡처 루프의 진행률 갱신이 리포트/프리뷰를 리렌더하지 않게)
  const pdfProgressRef = useRef<PdfProgressHandle>(null)
  // 생성 중단 요청 플래그. 상태가 아니라 ref 인 이유 — 캡처 루프가 setState 리렌더를
  // 기다리지 않고 즉시 최신값을 읽어야 한다.
  const pdfCancelRef = useRef(false)
  const [printBasicSet, setPrintBasicSet] = useState<PrintBasicSet>({
    contentWidth: CONTENT_WIDTH_MM,
    margin: 25,
    startX: 50,
    startY: 50,
    footerY: 60,
    footerFontSize: 8,
  })

  const { data: metricList } = useMetricList()
  const { data: selectedUnitList } = useUnitQuery()
  const defaultEnabled = !!filter.selectedActivityIds?.length && !!filter.selectedPlayerIds?.length

  // Report History — 리포트 조회 기록
  const trendHistoryPayload = useMemo<CreateReportHistoryPayload | null>(() => {
    if (!idfActivity?.length) return null
    return {
      reportType: REPORT_TYPE.TREND,
      idfTemplate: idfTemplate?.[0] ?? null,
      activityIds: idfActivity,
      playerIds: idfPlayer ?? [],
      dateStart,
      dateFinish,
      activityName: `Trend (${idfActivity.length} activities)`,
      templateName: null,
    }
  }, [idfActivity, idfPlayer, idfTemplate, dateStart, dateFinish])
  useRecordReportView(trendHistoryPayload, defaultEnabled)

  const { data: detail, isLoading: detailLoading } = useTrendPlayerActivitySummaryQuery(
    {
      idfActivityList: filter.selectedActivityIds ?? [],
      idfPlayerList: filter.selectedPlayerIds ?? [],
    },
    defaultEnabled,
  )
  const { data: playerSummary, isLoading: playerSummaryLoading } = useTrendPlayerSummaryQuery(
    {
      idfActivityList: filter.selectedActivityIds ?? [],
      idfPlayerList: filter.selectedPlayerIds ?? [],
    },
    defaultEnabled,
  )
  const { data: participatedPlayerList, isLoading: activityParticipatedPlayerLoading } =
    useActivityParticipatedPlayerListQuery(
      filter.selectedActivityIds ?? [],
      !!filter.selectedActivityIds?.length,
    )

  const { data: activeTemplate } = useReportTemplateDetailQuery(filter.selectedIdfTemplate[0])

  const positionRank = usePositionRank()
  const sectionSorts = createReportSortStore((state) => state.sorts)
  const syncReportSort = createReportSortStore((state) => state.syncTemplate)
  const syncReportColumnFilter = createReportColumnFilterStore((state) => state.syncTemplate)

  // 템플릿이 실제로 바뀔 때만 이전 화면 정렬·컬럼 필터(transient)를 폐기 (뒤로가기 복귀 등 재mount 시엔 보존)
  useEffect(() => {
    syncReportSort(REPORT_TYPE.TREND, filter.selectedIdfTemplate[0])
    syncReportColumnFilter(REPORT_TYPE.TREND, filter.selectedIdfTemplate[0])
  }, [filter.selectedIdfTemplate[0], syncReportSort, syncReportColumnFilter])

  useEffect(() => {
    if (!idfActivity?.length || !idfPlayer?.length) {
      setFilter(INIT_FILTER)
    } else {
      setFilter({
        selectedActivityIds: idfActivity || [],
        selectedPlayerIds: idfPlayer || [],
        selectedIdfTemplate: idfTemplate || [],
      })
    }
  }, [idfActivity, idfPlayer, idfTemplate, selectedIdfTeam])

  useEffect(() => {
    if (!playerSummary || !participatedPlayerList) {
      return
    }

    const newPlayersData = playerSummary.map((player) => {
      const participatedPlayer = participatedPlayerList.find(
        (item) => item.idfPlayer === player.idfPlayer,
      )

      return {
        ...player,
        image: participatedPlayer?.image || null,
      }
    })
    setPlayersData(newPlayersData)
  }, [playerSummary, participatedPlayerList])

  useEffect(() => {
    if (!detail || !playerSummary) {
      return
    }
    setSource({
      trendReportPlayers: playerSummary,
      trendActivityPlayerReport: detail,
    })
  }, [detail, playerSummary])

  useEffect(() => {
    initReport()
  }, [JSON.stringify(filter), metricList, source, template, selectedUnitList])

  useEffect(() => {
    if (activeTemplate) {
      setTemplate(activeTemplate)
    }
  }, [activeTemplate])

  const summaryData = useMemo(() => {
    if (!detail || !playerSummary || !dateStart || !dateFinish || !idfActivity || !playerSummary) {
      return DEFAULT_DETAIL_DATA
    }

    const newActivityKeyCount: { [key: string]: number } = {
      T: 0,
      L: 0,
      C: 0,
      F: 0,
      R: 0,
      E: 0,
    }

    detail.forEach((item) => {
      newActivityKeyCount[item.activityType] = newActivityKeyCount[item.activityType] + 1
    })

    return {
      name: `${globalDate(dateStart, true)} ~ ${globalDate(dateFinish, true)}`,
      activityCount: detail.length,
      playerCount: playerSummary.length,
      activityKeyCount: newActivityKeyCount,
    }
  }, [detail, playerSummary, dateStart, dateFinish, idfActivity, playerSummary])

  const initReport = async () => {
    if (!source || !metricList || !selectedUnitList || !template) {
      return
    }

    await createReport({
      template,
      source,
      metricList,
      t,
      translateKey,
      trendPlayers: playersData,
      setReportSections,
      selectedUnitList,
    })
  }

  const renderSection = () => {
    return reportSections.map((section, index) => {
      const reportLoading =
        detailLoading || playerSummaryLoading || activityParticipatedPlayerLoading

      if (section.type === SECTION_TYPE.PLAYER_GRAPH) {
        return (
          <ChartByPlayer
            key={`${section.type}-${index}`}
            reportSection={section as SectionGraph}
            loading={reportLoading}
            sectionIndex={index}
            reportType={REPORT_TYPE.TREND}
          />
        )
      }
      if (section.type === SECTION_TYPE.ACTIVITY_GRAPH) {
        return (
          <ChartByActivity
            key={`${section.type}-${index}`}
            reportSection={section as SectionGraph}
            loading={reportLoading}
            sectionIndex={index}
            reportType={REPORT_TYPE.TREND}
          />
        )
      }
      if (section.type === SECTION_TYPE.CUSTOM_TABLE) {
        const customTableSection = section as unknown as { type: string; settings: StatsSettings }

        // customTable 은 클라이언트 정렬만 — sortSetting 저장 미지원이라 템플릿 PUT(onSortChange) 미연결 (WEB-2193)
        return (
          <CustomTableSection
            key={`${section.type}-${index}`}
            settings={customTableSection.settings}
            reportType={REPORT_TYPE.TREND}
            sectionIndex={index}
          />
        )
      }

      return null
    })
  }

  // PDF 프리뷰용 정렬 데이터는 메모이즈해서 참조를 안정화한다.
  // 렌더마다 새 객체를 만들면 setPdfLoading 등 리렌더 시 차트가 다시 그려지는데,
  // 그 시점의 scaleRatio(첫 렌더 1 → 이후 컨테이너 실측 ~2)가 달라 확대-오버플로우로 차트가 빈 것처럼 보인다.
  const pdfSortedSections = useMemo(() => {
    return reportSections.map((section, index) => {
      if (!section) {
        return section
      }
      if (
        section.type === SECTION_TYPE.PLAYER_GRAPH ||
        section.type === SECTION_TYPE.ACTIVITY_GRAPH
      ) {
        return sortGraphSectionForExport(
          section as SectionGraph,
          positionRank,
          (chartIndex) => sectionSorts[buildReportSortKey(REPORT_TYPE.TREND, index, chartIndex)],
        )
      }

      return section
    })
  }, [reportSections, sectionSorts, positionRank, t])

  // 미리보기 페이지 배치. 차트가 다 그려진 뒤에 재야 높이가 맞으므로 레이아웃이 안정될 때까지
  // 기다렸다가 계산한다. 덩어리 위치는 margin 으로만 조정하므로 캡처 결과에는 영향이 없다.
  useEffect(() => {
    if (!previewOpen) {
      clearPreviewPagination(document.querySelector(".ExportPdfPreviewCont") as HTMLElement | null)
      setPreviewPages([])

      return
    }

    let cancelled = false

    const run = async () => {
      const target = await waitForPreviewContainer(".ExportPdfPreviewCont")
      if (!target || cancelled) {
        return
      }

      await document.fonts.ready
      await waitForStableLayout(target)

      if (cancelled) {
        return
      }

      setPreviewPages(applyPreviewPagination(target, printBasicSet.contentWidth, MEASURE_OPTIONS))
    }

    run()

    return () => {
      cancelled = true
    }
  }, [
    previewOpen,
    reportSections,
    pdfSortedSections,
    annotationOptions,
    decimalPoints,
    printBasicSet.contentWidth,
  ])

  const renderPdfSection = useCallback(() => {
    // DOM 을 재지 않는다 — 다이얼로그가 마운트되기 전 렌더에서는 1 이 잡혔다가 캡처 직전
    // 리렌더에서 실측값으로 바뀌어, 사용자가 본 미리보기와 실제 캡처가 달랐다.
    const scaleRatio = PDF_PREVIEW_WIDTH_PX / 1000

    return reportSections.map((section, index) => {
      if (section.type === SECTION_TYPE.PLAYER_GRAPH) {
        return (
          <PrintableChartByPlayerSection
            key={`pdf-${section.type}-${index}`}
            isFirst={index === 0}
            data={pdfSortedSections[index] as SectionGraph}
            currentDecimalPoint={decimalPoints}
            currentChartAnnotation={annotationOptions}
            scaleRatio={scaleRatio || 1}
          />
        )
      }
      if (section.type === SECTION_TYPE.ACTIVITY_GRAPH) {
        return (
          <PrintableChartByActivitySection
            key={`pdf-${section.type}-${index}`}
            isFirst={index === 0}
            data={pdfSortedSections[index] as SectionGraph}
            currentDecimalPoint={decimalPoints}
            currentChartAnnotation={annotationOptions}
            scaleRatio={scaleRatio || 1}
          />
        )
      }
      if (section.type === SECTION_TYPE.CUSTOM_TABLE) {
        const customTableSection = section as unknown as { type: string; settings: StatsSettings }

        return (
          <PrintableCustomTableSection
            key={`pdf-${section.type}-${index}`}
            settings={customTableSection.settings}
            sectionIndex={index}
            scaleRatio={scaleRatio || 1}
            currentDecimalPoint={decimalPoints}
            reportType={REPORT_TYPE.TREND}
            isFirst={index === 0}
          />
        )
      }
    })
  }, [reportSections, decimalPoints, annotationOptions, pdfSortedSections])

  const parseImage = async (html: HTMLElement): Promise<ImgData[]> => {
    const backGroundImg = new window.Image()
    const mapBoundingRect: Array<{ rect: DOMRect; type: string | undefined }> = []

    if (html.classList.contains("map")) {
    }

    const pdfContainer = document.querySelector(".ExportPdfPreviewCont")
    const containerWidth = pdfContainer?.clientWidth || html.scrollWidth

    // 표·상단 요약은 캡처 직전 화면에 띄워야 스크롤 폭/높이가 제대로 잡힌다.
    if (html.classList.contains("extracted-individual-table") || html.tagName === "TABLE") {
      html.scrollIntoView({ block: "start", behavior: "instant" })
      await new Promise((resolve) => setTimeout(resolve, 50))
    } else if (html.classList.contains("ReportInformationSummaryCont")) {
      html.scrollIntoView({ block: "start", behavior: "instant" })
      await new Promise((resolve) => setTimeout(resolve, 200))
    }

    // 캡처 박스와 mm 환산은 common/export/chunks 가 정한다 — 미리보기 페이징이 같은 값을 쓴다.
    // 페이지 비율(267/190) 패딩 없이 내용 크기 그대로 캡처한다. 페이지 높이로 패딩하면 흰 여백이
    // 이미지에 포함돼 남은공간 배치·분할 시 상단 대공백/백지 페이지/행 잘림으로 나타난다 (WEB-2264)
    const chunkBox = measureChunkBox(html, containerWidth, MEASURE_OPTIONS)
    const htmlWidth = chunkBox.htmlWidth
    const htmlHeight = chunkBox.htmlHeight

    await document.fonts.ready

    return html2canvas(html, {
      useCORS: true,
      allowTaint: true,
      scale: 1.2,
      height: htmlHeight,
      width: htmlWidth,
      windowHeight: htmlHeight,
      // 복제본은 미리보기와 같은 폭으로 레이아웃해야 한다. 캡처 대상의 폭(예: 분할된 표 250px)을
      // 주면 복제본 전체가 그 폭으로 눌려 표 컬럼이 min-content 로 찌그러진다.
      windowWidth: containerWidth,
      onclone: (clonedDoc) => {
        inlineStylesheetsIntoClone(clonedDoc)

        const chartContainers = clonedDoc.querySelectorAll(".element-chart-container")
        chartContainers.forEach((container) => {
          ;(container as HTMLElement).style.overflow = "visible"
        })

        const tableContainers = clonedDoc.querySelectorAll(".table-container")
        tableContainers.forEach((container) => {
          ;(container as HTMLElement).style.overflow = "visible"
        })

        // 미리보기와 같은 폭으로 고정한다 — 캡처 대상의 폭으로 잡으면 안 된다.
        // 분할된 표(예: 마지막 조각 250px)를 캡처할 때 컨테이너까지 250px 로 눌리면서
        // 컬럼이 min-content 로 찌그러지고(45/104/101 -> 19/104/19) 헤더가 세로로 풀려,
        // 표가 142x727 이 되어 캡처 범위 밖으로 행이 잘려 나갔다.
        const renewalContainers = clonedDoc.querySelectorAll(".renewal-pdf-container")
        renewalContainers.forEach((container) => {
          ;(container as HTMLElement).style.width = `${containerWidth}px`
          ;(container as HTMLElement).style.minWidth = `${containerWidth}px`
        })

        const sections = clonedDoc.querySelectorAll(".section")
        sections.forEach((section) => {
          ;(section as HTMLElement).style.width = `${containerWidth}px`
          ;(section as HTMLElement).style.minWidth = `${containerWidth}px`
        })
        const svgItems = clonedDoc.querySelectorAll("svg")
        const regexQuote = new RegExp("'", "g")
        const regexBackSpace = new RegExp("\b", "g")

        let container: HTMLElement | SVGElement | null = null
        svgItems.forEach((svg) => {
          const existingStyle = svg.querySelector("style")
          const fontStyle = `
            @import url('https://cdn.jsdelivr.net/npm/pretendard@latest/dist/web/static/pretendard.min.css');
            text, tspan { font-family: 'Pretendard', 'pretendard', sans-serif !important; }
          `
          if (existingStyle) {
            existingStyle.textContent = fontStyle + existingStyle.textContent
          } else {
            const styleElement = document.createElementNS("http://www.w3.org/2000/svg", "style")
            styleElement.textContent = fontStyle
            svg.insertBefore(styleElement, svg.firstChild)
          }

          container = svg
          while (container && container.parentElement) {
            container = container.parentElement
            if (container.classList.value.includes("renewal-pdf map")) {
              if (html.classList.contains("empty")) {
                svg.style.filter = "blur(10px)"
              }
              const isLocation = container.classList.value.includes("location")
              const isFormation = container.classList.value.includes("formation")

              let type: string | undefined
              if (isLocation) {
                type = "location"
              } else if (isFormation) {
                type = "formation"
              } else {
                type = undefined
              }

              mapBoundingRect.push({ rect: container.getBoundingClientRect(), type })
              break
            }
          }
          svg.innerHTML = svg.innerHTML.replace(regexBackSpace, "").replace(regexQuote, "&apos;")
        })
      },
    }).then((canvas) => {
      const image: ImgData[] = []
      const { imgWidth, imgHeight } = chunkImageSizeMm(
        chunkBox,
        containerWidth,
        printBasicSet.contentWidth,
      )
      html.setAttribute("data-html2canvas-ignore", "")

      const isLocation = html.classList.value.includes("renewal-pdf map location")
      const isFormation = html.classList.value.includes("renewal-pdf map formation")

      if (html.classList.contains("map") && backGroundImg.src) {
        const ctx = canvas.getContext("2d")
        if (ctx && html.classList.contains("empty")) {
          ctx.filter = "blur(10px)"
        }
        let rect
        if (isLocation) {
          rect = mapBoundingRect.find((item) => item.type === "location")?.rect
        } else if (isFormation) {
          rect = mapBoundingRect.find((item) => item.type === "formation")?.rect
        } else {
          rect = mapBoundingRect.find((item) => item.type === undefined)?.rect
        }

        const mapElement = html.children[2]
        const sectionTitleElement = html.children[0]
        const firstPaddingTop = parseFloat(
          window.getComputedStyle(mapElement).getPropertyValue("padding-top"),
        )

        ctx?.drawImage(
          backGroundImg,
          (rect?.width as number) / 2 + (rect?.left as number) - backGroundImg.width / 2,
          (rect?.top as number) +
            mapElement.getBoundingClientRect().top -
            sectionTitleElement.getBoundingClientRect().top +
            firstPaddingTop,
          backGroundImg.width,
          backGroundImg.height,
        )

        if (isLocation && mapElement.children[1]) {
          const secondPaddingTop = parseFloat(
            window.getComputedStyle(mapElement.children[1]).getPropertyValue("padding-top"),
          )
          ctx?.drawImage(
            backGroundImg,
            (rect?.width as number) / 2 + (rect?.left as number) - backGroundImg.width / 2,
            (rect?.top as number) +
              mapElement.children[1].getBoundingClientRect().top -
              sectionTitleElement.getBoundingClientRect().top +
              secondPaddingTop,
            backGroundImg.width,
            backGroundImg.height,
          )
        }
      }

      const imgData = canvas.toDataURL("image/jpeg", 0.85)

      image.push({
        data: imgData,
        width: imgWidth,
        height: imgHeight,
        class: html.classList,
        chunkType: chunkTypeOf(html),
      })

      canvas.width = 0
      canvas.height = 0

      return image
    })
  }

  // 페이지 배치는 common/export/paginate 가 전담한다 (daily 와 동일 규칙).
  //
  // 이전에는 여기서 mm 공간에 이미지를 잘라 붙였고, 그 과정에서
  //   - 한 페이지에 통째로 들어가는 덩어리도 남은 자리가 모자라면 중간에서 잘렸고
  //   - 콘텐츠 하한(282mm)이 푸터 위치(281.1mm)보다 아래라 페이지 번호와 겹칠 수 있었으며
  //   - 첫 페이지와 이후 페이지의 콘텐츠 구간이 서로 달랐다
  // daily 가 a1b112e1 에서 정리한 규칙을 그대로 쓴다 — 백지 페이지가 구조적으로 안 생긴다.
  const makeReportPdf = async (imageDataList: ImgData[]) => {
    const doc = await createJsPdf({ orientation: "portrait", format: "a4" })
    // Pretendard(커스텀) 폰트는 추가하지 않는다.
    // jsPDF 는 출력(buildDocument) 시 추가된 모든 커스텀 폰트를 임베드/서브셋하는데,
    // CDN 로드 실패/OTF 등으로 글리프 데이터가 없으면 glyphFor 에서 크래시해 PDF 생성이 통째로 실패한다.
    // 리포트 본문은 전부 이미지(html2canvas), 텍스트는 footer 페이지번호뿐이라 내장 helvetica 로 충분.
    doc.setFont("helvetica", "normal")

    const pageWidthMM = A4_WIDTH_MM
    const pageHeightMM = A4_HEIGHT_MM
    const xOffset = (pageWidthMM - printBasicSet.contentWidth) / 2

    const { placements } = paginate(
      imageDataList.map((imageData) => imageData.height),
      {
        types: imageDataList.map((imageData) => imageData.chunkType ?? CHUNK_TYPE.CHART),
      },
    )

    let currentPage = 1

    const drawFooter = () => {
      const footerText = `${currentPage}`
      doc.setFontSize(printBasicSet.footerFontSize)
      doc.setTextColor(colors.neutral.grey["800"])

      const textWidth = doc.getTextWidth(footerText)
      doc.text(footerText, (pageWidthMM - textWidth) / 2, pageHeightMM - PAGE_MARGIN_MM)
    }

    const goToPage = (page: number) => {
      while (currentPage < page) {
        drawFooter()
        doc.addPage()
        currentPage++
      }
    }

    const loadImage = (src: string) =>
      new Promise<HTMLImageElement>((resolve) => {
        const img = new window.Image()
        img.src = src
        if (img.complete) {
          resolve(img)

          return
        }
        img.onload = () => resolve(img)
      })

    for (const placement of placements) {
      if (!placement.placed) {
        continue
      }

      const imageData = imageDataList[placement.index]

      if (placement.slices.length === 0) {
        goToPage(placement.page)
        doc.addImage(
          imageData.data,
          "JPEG",
          xOffset,
          placement.y,
          imageData.width,
          placement.height,
        )
        continue
      }

      // 한 페이지보다 큰 덩어리 — 조각마다 원본에서 해당 구간만 잘라 그린다.
      const img = await loadImage(imageData.data)
      const pdfToImgRatio = img.height / placement.height

      for (const slice of placement.slices) {
        goToPage(slice.page)

        const canvas = document.createElement("canvas")
        canvas.width = img.width
        canvas.height = slice.height * pdfToImgRatio

        const ctx = canvas.getContext("2d")
        if (ctx) {
          ctx.fillStyle = colors.neutral.white
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(
            img,
            0,
            slice.sourceOffset * pdfToImgRatio,
            img.width,
            canvas.height,
            0,
            0,
            img.width,
            canvas.height,
          )
        }

        doc.addImage(
          canvas.toDataURL("image/jpeg", 0.85),
          "JPEG",
          xOffset,
          slice.y,
          imageData.width,
          slice.height,
        )
      }
    }

    drawFooter()

    // window.open(blob) 은 긴 캡처(async) 뒤 호출이라 브라우저 팝업 차단에 걸려 큰 리포트에서 PDF 가
    // 안 열렸다. 팝업 차단 안 걸리는 다운로드로 전달을 보장한다.
    doc.save("trend_report.pdf")
  }

  const generatePdf = async () => {
    pdfCancelRef.current = false
    setPdfLoading(true)
    pdfProgressRef.current?.start()

    await new Promise((resolve) => setTimeout(resolve, 100))

    await document.fonts.ready

    const container = document.querySelector(".ExportPdfPreviewCont")

    // 이전 실행에서 남긴 제외 표시 제거 (재시도 시 빈 캡처 방지). 캡처 대상 밖에도 붙으므로 문서 전역으로 지운다.
    clearHtml2CanvasIgnore()

    // 캡처 대상 경로 밖(앱 셸·라이브 리포트·다이얼로그 배경)을 제외해 복제 범위를 좁힌다.
    // html2canvas 는 호출마다 문서를 통째로 복제하고, 그 비용이 생성 시간의 대부분을 차지한다.
    markHtml2CanvasIgnoreOutside(container)

    try {
      const htmlElements = collectPdfTargets(container)

      const imageDataList: ImgData[] = []
      const totalSections = htmlElements.length

      for (let i = 0; i < htmlElements.length; i++) {
        // 취소는 섹션 경계에서만 확인한다 — html2canvas 가 메인 스레드를 점유해
        // 한 섹션을 캡처하는 동안에는 이벤트 루프가 돌지 않는다.
        if (pdfCancelRef.current) {
          return
        }

        const currentSection = i + 1
        const progress = Math.floor(10 + (currentSection / totalSections) * 80)

        pdfProgressRef.current?.setProgress(progress)

        await new Promise((resolve) => setTimeout(resolve, 50))

        const sectionResult = await parseImage(htmlElements[i])
        imageDataList.push(...sectionResult)
      }

      if (pdfCancelRef.current) {
        return
      }

      pdfProgressRef.current?.setProgress(95)

      await new Promise((resolve) => setTimeout(resolve, 200))

      await makeReportPdf(imageDataList as ImgData[])

      pdfProgressRef.current?.setProgress(100)
    } catch (error) {
      alert(error)
    } finally {
      clearHtml2CanvasIgnore()

      // 취소 시에는 완료 연출(800ms) 없이 즉시 닫는다.
      const closeDelay = pdfCancelRef.current ? 0 : 800
      setTimeout(() => {
        setPdfLoading(false)
        pdfProgressRef.current?.finish()
      }, closeDelay)
    }
  }

  return (
    <div className="TrendReportContainer">
      <div
        className="trend-report-setting-icon"
        onClick={() => router?.push("/settings/report/trend")}>
        <Icon type="settings" size={28} color={colors.neutral.grey[600]} />
      </div>
      <ReportFilter reportType={REPORT_TYPE.TREND} onClickViewReport={() => {}} />
      <InformationSummary
        type={REPORT_TYPE.TREND}
        data={summaryData}
        playersData={playersData ?? []}
        onClickExportPDF={() => setExportPdfOpen(true)}
      />
      {renderSection()}

      <ExportChartDialog
        open={exportPdfOpen}
        setOpen={setExportPdfOpen}
        decimalPoints={currentDecimalPoints}
        annotationOptions={currentAnnotationOptions}
        onChangeDecimalPoints={setCurrentDecimalPoints}
        onChangeAnnotationOptions={setCurrentAnnotationOptions}
        onConfirm={() => {
          setDecimalPoints(currentDecimalPoints)
          setAnnotationOptions(currentAnnotationOptions)
          setPreviewOpen(true)
        }}
      />
      <Dialog
        open={previewOpen}
        title={t("preview")}
        footer={
          <>
            <BasicButton
              type="default-outlined"
              onClick={() => setPreviewOpen(false)}
              size="md"
              disabled={pdfLoading}>
              {t("button.close")}
            </BasicButton>
            <BasicButton
              onClick={generatePdf}
              size="md"
              type="primary-contained"
              disabled={pdfLoading}>
              {t("pdfPrint")}
            </BasicButton>
          </>
        }
        onClose={() => {
          // 생성 중에는 배경 클릭·ESC·닫기 버튼으로 닫히지 않는다.
          // 캡처 대상 DOM 이 이 다이얼로그 안에 있어, 닫히면 생성이 그대로 깨진다.
          if (pdfLoading) {
            return
          }
          setPreviewOpen(false)
        }}>
        <div
          className="ExportPdfPreviewCont"
          style={{
            width: PDF_PREVIEW_WIDTH_PX,
            minWidth: PDF_PREVIEW_WIDTH_PX,
            margin: "0 auto",
            // 마지막 페이지 박스는 absolute 라 컨테이너 높이에 안 잡힌다 — 직접 확보한다.
            minHeight: previewPages.length
              ? `${previewPages[previewPages.length - 1].topPx + previewPages[previewPages.length - 1].heightPx}px`
              : undefined,
          }}>
          <PreviewPageLayer pages={previewPages} />
          <style>{`
            .ExportPdfPreviewCont .ReportSectionCont {
              box-shadow: none !important;
            }
            .ExportPdfPreviewCont .section-name {
              background-color: white !important;
            }
            .ExportPdfPreviewCont .ReportSectionTitleCont {
              background-color: white !important;
            }
          `}</style>
          <InformationSummary
            type={REPORT_TYPE.TREND}
            data={summaryData}
            playersData={playersData ?? []}
            isDialog
          />
          {renderPdfSection()}

          {/* PDF 생성 로딩 오버레이 (진행률 상태 격리) */}
          <PdfProgressOverlay
            ref={pdfProgressRef}
            onCancel={() => {
              pdfCancelRef.current = true
            }}
          />
        </div>
      </Dialog>
    </div>
  )
}

const createReport = async ({
  template,
  source,
  metricList,
  t,
  translateKey,
  trendPlayers,
  setReportSections,
  selectedUnitList,
}: {
  template: ReportTemplate
  source: TrendReportSourceType
  trendPlayers: TrendPlayerReport[]
  setReportSections: Dispatch<SetStateAction<TypeSection[]>>
  selectedUnitList: Unit[]
  translateKey: (key: string) => string | undefined
} & Pick<ReportCommonProps, "metricList" | "t">) => {
  if (!template || !template.content) {
    return
  }

  const newTemplateContent: Section[] = []
  for (const section of template.content) {
    if (isArray(section.settings)) {
      section.settings.forEach((setting) => {
        newTemplateContent.push({
          section: section.section,
          active: section.active,
          settings: [setting],
        })
      })
    } else {
      newTemplateContent.push(section)
    }
  }

  const metricMap: MetricMapType =
    metricList?.reduce((map, obj) => {
      map.set(obj.key, obj)

      return map
    }, new Map()) || new Map()

  removeSpecialCharacterInAllSource(source)

  const reportSectionsNew: Array<TypeSection> = []

  for (const [index, section] of newTemplateContent.entries()) {
    if (!section || !section.active) {
      continue
    }
    switch (section.section) {
      case SECTION_TYPE.ACTIVITY_GRAPH:
        {
          const setting = section.settings as GraphSettings[]
          if (setting) {
            const sectionGraph = createSectionActivityGraph({
              graphSettings: setting,
              metricList,
              metricMap,
              t,
              translateKey,
              source,
              selectedUnitList,
            })
            if (sectionGraph) {
              reportSectionsNew[index] = sectionGraph
            }
          }
        }
        break

      case SECTION_TYPE.PLAYER_GRAPH:
        {
          const setting = section.settings as GraphSettings[]
          if (setting) {
            const sectionGraph = createSectionPlayerGraph({
              graphSettings: setting,
              metricList,
              t,
              translateKey,
              trendReportPlayers: trendPlayers,
              metricMap,
              selectedUnitList,
            })
            if (sectionGraph) {
              reportSectionsNew[index] = sectionGraph
            }
          }
        }
        break

      case SECTION_TYPE.CUSTOM_TABLE:
        {
          const setting = section.settings as StatsSettings
          if (setting) {
            reportSectionsNew[index] = {
              type: SECTION_TYPE.CUSTOM_TABLE,
              settings: setting,
            } as unknown as TypeSection
          }
        }
        break
    }
  }

  setReportSections(reportSectionsNew)
}

const createSectionPlayerGraph = ({
  graphSettings,
  metricList,
  t,

  translateKey,
  trendReportPlayers,
  metricMap,
  selectedUnitList,
}: {
  graphSettings: GraphSettings[]
  trendReportPlayers: TrendPlayerReport[]
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  translateKey: (key: string) => string | undefined
} & Pick<ReportCommonProps, "metricList" | "t">): SectionGraph | null => {
  if (isEmpty(graphSettings) || isEmpty(metricList)) {
    return null
  }

  const settings = structuredClone(graphSettings)

  const charts = settings.map((settingsItem) => {
    const sort = settingsItem.sortSetting
      ? new Sort(
          settingsItem.sortSetting.key,
          settingsItem.sortSetting.order.toUpperCase() as "ASC" | "DESC",
        )
      : new Sort("uniformNumber", SORT_ORDER.ASC)
    const axisY: AxisY[] = settingsItem.yAxis.map((y) => {
      const metric = metricList.find((metric) => metric.key === y.metric)
      const unit =
        metric && metric.key ? (parseUnit(metric.key, selectedUnitList, metricMap) ?? null) : null
      let decimalPoint = getDecimalPoint(metric?.key, unit)

      if (settingsItem.calcType === "average" && unit === "times") {
        decimalPoint = 1
      }

      return new AxisY({
        name: translateKey(y.metric) || t("metric." + y.metric).toString(),
        key: y.metric,
        unit,
        decimalPoint,
        color: y.color,
        chartType: y.chartType,
        flagShow: true,
        manualScaleActive: y.manualScaleActive,
        manualScaleValue: y.manualScaleValue,
      })
    })

    const players = structuredClone(trendReportPlayers)

    const playerData = players?.map(({ total, average, ...player }) => {
      const map = new Map<string, string | number | null>()
      map.set("uniformNumber", player.uniformNumber)
      map.set("name", player.playerName)
      map.set("position", player.position)
      map.set("activity", player.numOfActivity)

      const sourceRaw = settingsItem.calcType === "total" ? total : average
      const source = parseSummary(sourceRaw)
      settingsItem.yAxis.forEach((y) => {
        const { value } = getSummaryImplValue(y.metric, source, metricMap)
        map.set(y.metric, value)
      })

      return map
    })

    const dataChart: Array<ChartRow> = []
    playerData?.forEach((player) => {
      const row = new ChartRow()

      row.name = `${player.get("uniformNumber")} ${player.get("name")}`

      row.header.set("uniformNumber", player.get("uniformNumber") ?? null)
      row.header.set("name", player.get("name") ?? null)
      row.header.set("position", player.get("position") ?? null)
      row.header.set("activity", player.get("activity") ?? null)

      const isGK = player.get("position") === "GK"
      settingsItem.yAxis.forEach((y) => {
        if (!isGK && DIVE_METRICS.includes(y.metric)) {
          row.data.push(null)

          return
        }
        const summaryVal = player.get(y.metric)
        const value = !isNil(summaryVal) ? Number(summaryVal) : null
        row.data.push(value)
      })

      dataChart.push(row)
    })

    return new ChartData(settingsItem, sort, axisY, dataChart)
  })

  return new SectionGraph(SECTION_TYPE.PLAYER_GRAPH, charts)
}

const removeSpecialCharacterInAllSource = (source: TrendReportSourceType) => {
  if (!isEmpty(source.trendReportPlayers)) {
    source.trendReportPlayers.forEach((player) => {
      if (player && player.playerName) {
        player.playerName = removeSpecialCharacter(player.playerName)
      }
    })
  }

  if (!isEmpty(source.trendActivityPlayerReport)) {
    source.trendActivityPlayerReport.forEach((activity) => {
      if (activity && activity.activityName) {
        activity.activityName = removeSpecialCharacter(activity.activityName)
      }
    })
  }
}

const createSectionActivityGraph = ({
  graphSettings,
  metricList,
  metricMap,
  t,
  translateKey,
  source,
  selectedUnitList,
}: {
  graphSettings: GraphSettings[]
  source: TrendReportSourceType
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  translateKey: (key: string) => string | undefined
} & Pick<ReportCommonProps, "metricList" | "t">): SectionGraph | null => {
  if (isEmpty(graphSettings) || isEmpty(metricList)) {
    return null
  }

  const settings = structuredClone(graphSettings)

  const charts = settings.map((settingsItem) => {
    const sort = settingsItem.sortSetting
      ? new Sort(
          settingsItem.sortSetting.key,
          settingsItem.sortSetting.order.toUpperCase() as "ASC" | "DESC",
        )
      : new Sort("date", SORT_ORDER.ASC)
    const axisY: AxisY[] = settingsItem.yAxis.map((y) => {
      const metric = metricList.find((metric) => metric.key === y.metric)
      const unit =
        metric && metric.key ? (parseUnit(metric.key, selectedUnitList, metricMap) ?? null) : null
      let decimalPoint = getDecimalPoint(metric?.key, unit)

      if (settingsItem.calcType === "average" && unit === "times") {
        decimalPoint = 1
      }

      return new AxisY({
        name: translateKey(y.metric) || t("metric." + y.metric).toString(),
        key: y.metric,
        unit,
        decimalPoint,
        color: y.color,
        chartType: y.chartType,
        flagShow: true,
        manualScaleActive: y.manualScaleActive,
        manualScaleValue: y.manualScaleValue,
      })
    })

    const activities = structuredClone(source.trendActivityPlayerReport)

    const dataChart: Array<ChartRow> = []
    activities?.forEach(({ average, total, ...activity }) => {
      const row = new ChartRow()

      const date = globalDate(activity.date, true)

      row.name = date

      row.header.set("name", activity.activityName)
      row.header.set("date", date)
      row.header.set("playerCount", activity.numOfPlayers)

      const sourceRaw = settingsItem.calcType === "total" ? total : average

      const source = parseSummary(sourceRaw)
      settingsItem.yAxis.forEach((y) => {
        const { value } = getSummaryImplValue(y.metric, source, metricMap)
        row.data.push(value)
      })

      dataChart.push(row)
    })

    return new ChartData(settingsItem, sort, axisY, dataChart)
  })

  return new SectionGraph(SECTION_TYPE.ACTIVITY_GRAPH, charts)
}
